// Data hooks of the PV screens (COMX-FE-04). The generated client cannot express `If-Match`, so
// the PATCH goes through the runtime fetcher (like the quotes). The rest wraps the generated hooks.
import {
  getMinutesControllerGetQueryKey,
  getMinutesControllerListQueryKey,
  minutesControllerPdf,
  useMinutesControllerCreate,
  useMinutesControllerGet,
  useMinutesControllerList,
  useMinutesControllerPublish,
  useProjectsControllerListMembers,
  type MeetingMinutesDtoOutput,
  type MinutesControllerListParams,
  type UpdateMinutesDto,
} from '@batiflow/api-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRuntime } from '@/app/providers/runtime-provider';
import { etagOf } from './model';

export const LIST_SIZE = 50;

/** The PV. Not retried: a 403 or a 404 is the answer. */
export function useMinutes(spaceId: string, minutesId: string) {
  return useMinutesControllerGet(spaceId, minutesId, { query: { retry: false } });
}

/** The PVs of a space, newest first (drafts only for the people who can contribute). */
export function useSpaceMinutes(spaceId: string, params: MinutesControllerListParams = {}) {
  return useMinutesControllerList(spaceId, { limit: LIST_SIZE, ...params });
}

export const useCreateMinutes = useMinutesControllerCreate;

/** Stores an answer of the server as the cached PV and refreshes the lists. */
export function useCacheMinutes(spaceId: string) {
  const queryClient = useQueryClient();
  return (minutes: MeetingMinutesDtoOutput): void => {
    queryClient.setQueryData(getMinutesControllerGetQueryKey(spaceId, minutes.id), minutes);
    void queryClient.invalidateQueries({ queryKey: getMinutesControllerListQueryKey(spaceId) });
  };
}

export interface UpdateMinutesVariables {
  /** `version` of the PV as loaded: sent as `If-Match` (optimistic locking). */
  version: number;
  data: UpdateMinutesDto;
}

/** PATCH of a draft with `If-Match`. */
export function useUpdateMinutes(spaceId: string, minutesId: string) {
  const { fetcher } = useRuntime();
  return useMutation({
    mutationFn: async ({ version, data }: UpdateMinutesVariables) => {
      const response = await fetcher<MeetingMinutesDtoOutput>({
        method: 'PATCH',
        url: `/api/v1/spaces/${spaceId}/meeting-minutes/${minutesId}`,
        data,
        etag: etagOf(version),
      });
      return response.data;
    },
  });
}

/** Publishes (idempotent: a second call answers the published PV). */
export const usePublishMinutes = useMinutesControllerPublish;

/** A short-lived link to the published PV's PDF; 409 PDF_NOT_READY while the worker renders it. */
export const fetchMinutesPdf = minutesControllerPdf;

/**
 * The members of the project, to pick the assignee of an action. Only for the organisation that
 * holds the project: another organisation's partner gets a refusal and sees no picker.
 */
export function useAssignableMembers(
  orgId: string | null,
  projectId: string | null,
  enabled: boolean,
) {
  return useProjectsControllerListMembers(orgId ?? '', projectId ?? '', {
    query: { enabled: enabled && orgId !== null && projectId !== null, retry: false },
  });
}
